import { useEffect, useMemo, useState } from 'react';
import {
  detectAuditaloMetadataFromFilename,
  processEstadosFinancierosFile,
  releaseDownloadUrl
} from '../services/estadosFinancierosApi';

function formatFileSize(file) {
  if (!file) return 'Sin archivo seleccionado';
  const sizeKb = Math.max(1, Math.round(file.size / 1024));
  return `${file.name} · ${sizeKb.toLocaleString('es-MX')} KB`;
}

function formatNumber(value, decimals = 2) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return '0';
  return numericValue.toLocaleString('es-MX', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
}

function formatMoney(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return '$0.00';
  return numericValue.toLocaleString('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

function formatGeneratedAt(value) {
  if (!value) return 'Fecha no reportada';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' });
}

function formulaRuntimeLabel(result) {
  if (result?.formulaValidationMode === 'static_only' || !result?.formulaRecalculationPerformed) {
    return 'Solo validacion estatica';
  }
  if (result.formulaRecalculationEngine && result.formulaEvaluatedErrorCount === 0) {
    return `Recalculado con ${result.formulaRecalculationEngine}`;
  }
  return 'Recalculo con alertas';
}

function StatusPill({ state, children }) {
  return <span className={`financial-status-pill ${state}`}>{children}</span>;
}

function ValidationRow({ label, status, detail, state }) {
  return (
    <div className="financial-validation-row">
      <StatusPill state={state}>{status}</StatusPill>
      <div>
        <strong>{label}</strong>
        <small>{detail}</small>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, detail, state = 'default' }) {
  return (
    <div className={`financial-summary-card ${state}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      {detail && <small>{detail}</small>}
    </div>
  );
}

function getRunSummary({ selectedFile, result, processing, errorMessage, hasDownload }) {
  if (processing) {
    return {
      state: 'planned',
      label: 'Procesando',
      title: 'Revisando la balanza',
      detail: 'Estamos validando periodo, estructura, cuadre y generacion de los estados financieros.'
    };
  }

  if (errorMessage) {
    return {
      state: 'danger',
      label: 'Revisar',
      title: 'No fue posible procesar',
      detail: errorMessage
    };
  }

  if (!selectedFile) {
    return {
      state: 'planned',
      label: 'Pendiente',
      title: 'Carga una balanza Auditalo',
      detail: 'El archivo puede venir como .xls o .xlsx.'
    };
  }

  if (!result) {
    return {
      state: 'ready',
      label: 'Listo',
      title: 'Archivo listo para procesar',
      detail: 'Confirma que el periodo y RFC detectados sean correctos antes de generar los estados financieros.'
    };
  }

  if (hasDownload && result.validationOk && !result.runtimeWarnings?.length) {
    return {
      state: 'ready',
      label: 'Estados listos',
      title: 'Estados financieros generados',
      detail: 'Descarga el XLSX y revisalo antes de usarlo como archivo final.'
    };
  }

  if (hasDownload && result.validationOk && result.runtimeWarnings?.length) {
    return {
      state: 'warning',
      label: 'Revisar runtime',
      title: 'Estados descargables con advertencias',
      detail: 'La salida existe, pero revisa la identidad del backend y el modo de recalculo antes de usarla.'
    };
  }

  if (result.generated) {
    return {
      state: result.validationOk ? 'warning' : 'danger',
      label: result.validationOk ? 'Generado' : 'Con alertas',
      title: 'Estados generados sin enlace directo',
      detail: 'El servicio reporto el archivo, pero falta un enlace descargable para el navegador.'
    };
  }

  return {
    state: 'warning',
    label: 'Con alertas',
    title: 'La corrida requiere revision',
      detail: 'Revisa las validaciones antes de intentar descargar los estados financieros.'
  };
}

function EstadosFinancierosView() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [result, setResult] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const downloadUrl = result?.downloadUrl;
    return () => releaseDownloadUrl(downloadUrl);
  }, [result?.downloadUrl]);

  const filenameMetadata = useMemo(
    () => detectAuditaloMetadataFromFilename(selectedFile?.name || ''),
    [selectedFile]
  );

  const canProcess = Boolean(selectedFile && !processing);

  const handleFileChange = event => {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);
    setResult(null);
    setErrorMessage('');
  };

  const handleProcess = async () => {
    if (!selectedFile) return;
    setProcessing(true);
    setErrorMessage('');
    try {
      const response = await processEstadosFinancierosFile(selectedFile);
      setResult(response);
    } catch (error) {
      setResult(null);
      setErrorMessage(error?.message || 'No fue posible procesar el archivo Auditalo.');
    } finally {
      setProcessing(false);
    }
  };

  const periodFilename = result?.periodFromFilename || filenameMetadata.periodFromFilename || 'No detectado';
  const periodContent = result?.periodFromContent || 'Pendiente de procesar';
  const rfc = result?.rfc || filenameMetadata.rfc || 'No detectado';
  const razonSocial = result?.razonSocial || (result ? 'No disponible en la respuesta actual' : 'Pendiente de procesar');
  const missingAccountsCount = result?.missingAccounts?.length || 0;
  const hasDownload = Boolean(result?.downloadUrl);
  const runSummary = getRunSummary({ selectedFile, result, processing, errorMessage, hasDownload });
  const validationRows = [
    {
      label: 'Periodo',
      status: result ? (result.periodMatches ? 'Coincide' : 'Revisar') : 'Pendiente',
      state: result ? (result.periodMatches ? 'ready' : 'warning') : 'planned',
      detail: result
        ? result.periodMatches
          ? `Nombre y balanza indican ${periodContent}.`
          : `Nombre: ${periodFilename}. Balanza: ${periodContent}.`
        : 'Se valida contra el periodo declarado dentro del archivo.'
    },
    {
      label: 'Estructura',
      status: result ? (result.structureOk ? 'Valida' : 'Revisar') : 'Pendiente',
      state: result ? (result.structureOk ? 'ready' : 'warning') : 'planned',
      detail: result
        ? result.structureOk
          ? `Se leyeron ${result.normalizedRows} registros de la balanza.`
          : 'Faltan columnas o datos necesarios para armar el estado financiero.'
        : 'Debe incluir cuenta, saldo inicial, debe, haber y saldo final.'
    },
    {
      label: 'Cuentas por revisar',
      status: result ? (missingAccountsCount ? `${missingAccountsCount}` : 'Sin pendientes') : 'Pendiente',
      state: result ? (missingAccountsCount ? 'warning' : 'ready') : 'planned',
      detail: missingAccountsCount
        ? result.missingAccounts.slice(0, 3).join(', ')
        : result
          ? 'No se reportaron cuentas faltantes para los estados financieros.'
          : 'Aqui apareceran cuentas sin mapeo o clasificacion.'
    },
    {
      label: 'Cuadre',
      status: result ? (result.cuadra ? 'Cuadra' : 'No cuadra') : 'Pendiente',
      state: result ? (result.cuadra ? 'ready' : 'danger') : 'planned',
      detail: result
        ? `Diferencia ${formatMoney(result.differenceCuadre)}. Tolerancia menor a ${formatMoney(result.tolerance)}.`
        : 'La diferencia permitida debe ser menor a $1.00.'
    },
    {
      label: 'Archivo de estados',
      status: result ? (hasDownload ? 'Descargable' : result.generated ? 'Generado' : 'Pendiente') : 'Pendiente',
      state: result ? (hasDownload ? 'ready' : result.generated ? 'warning' : 'planned') : 'planned',
      detail: hasDownload
        ? 'El XLSX procesado esta listo para descargar.'
        : result?.generated
          ? 'El backend genero el archivo; falta exponer enlace de descarga.'
          : 'Se habilita cuando termine el procesamiento.'
    }
    ,
    {
      label: 'Hojas del libro',
      status: result ? (result.sheetNamesMatchExpected ? 'BG + ER + BAL' : 'Revisar') : 'Pendiente',
      state: result ? (result.sheetNamesMatchExpected ? 'ready' : 'warning') : 'planned',
      detail: result
        ? result.sheetNamesMatchExpected
          ? 'El libro contiene Balance General, Estado de Resultados y Balanza.'
          : 'El backend debe reportar exactamente BG, ER y BAL en ese orden.'
        : 'Se valida al recibir la salida del backend.'
    }
  ];

  return (
    <section className="financial-view" data-ui-view="estados-financieros" data-ui-section="financial-view">
      <div className="financial-hero">
        <div>
          <p className="eyebrow">Desarrollo · Estados financieros</p>
          <h2>Estados financieros desde balanza Auditalo</h2>
          <p className="subtitle">
            Balance General + Estado de Resultados + Balanza en un solo XLSX para revision contable.
          </p>
        </div>
        <div className="financial-runtime-card" aria-label="Estado de procesamiento">
          <StatusPill state={runSummary.state}>{runSummary.label}</StatusPill>
          <strong>{runSummary.title}</strong>
          <small>{runSummary.detail}</small>
          {result && (
            <div className="financial-runtime-meta" data-testid="financial-runtime-meta">
              <span>
                Backend: {result.backendVersion || 'No reportado'}
                {result.backendCommit ? ` · commit ${result.backendCommit.slice(0, 8)}` : ''}
              </span>
              <span>
                Generador: {result.generatorProfile || 'No reportado'} {result.generatorProfileVersion || ''}
              </span>
              <span>Formula: {formulaRuntimeLabel(result)}</span>
            </div>
          )}
        </div>
      </div>

      <div className="financial-layout">
        <div className="financial-main">
          <article className="financial-panel">
            <div className="financial-panel-head">
              <div>
                <p className="financial-kicker">Paso 1</p>
                <h3>Cargar archivo XLS</h3>
              </div>
              <StatusPill state={selectedFile ? 'ready' : 'planned'}>
                {selectedFile ? 'Archivo listo' : 'Pendiente'}
              </StatusPill>
            </div>

            <label className={`financial-upload-card ${selectedFile ? 'selected' : ''}`} htmlFor="financial-auditalo-file">
              <strong>Seleccionar balanza de Auditalo</strong>
              <span>{formatFileSize(selectedFile)}</span>
              <input
                id="financial-auditalo-file"
                type="file"
                accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={handleFileChange}
              />
            </label>

            <div className="financial-helper-strip">
              <span>Formato esperado: .xls o .xlsx</span>
              <span>Un archivo por corrida</span>
              <span>El periodo se confirma con el contenido</span>
            </div>

            <div className="financial-detected-grid">
              <SummaryCard label="Empresa" value={razonSocial} detail={rfc !== 'No detectado' ? `RFC ${rfc}` : 'RFC pendiente'} />
              <SummaryCard
                label="Periodo"
                value={periodContent !== 'Pendiente de procesar' ? periodContent : periodFilename}
                detail={result ? `Balanza: ${periodContent}` : 'Detectado desde el nombre del archivo'}
                state={result?.periodMatches ? 'ready' : result ? 'warning' : 'default'}
              />
              <SummaryCard
                label="Registros leidos"
                value={result ? formatNumber(result.normalizedRows, 0) : 'Pendiente'}
                detail="Filas utiles de la balanza"
              />
              <SummaryCard
                label="Cuadre"
                value={result ? (result.cuadra ? 'Correcto' : 'Revisar') : 'Pendiente'}
                detail={result ? `Diferencia ${formatMoney(result.differenceCuadre)}` : 'Tolerancia menor a $1.00'}
                state={result ? (result.cuadra ? 'ready' : 'danger') : 'default'}
              />
            </div>

            {errorMessage && <p className="financial-feedback error">{errorMessage}</p>}

            {result?.runtimeWarnings?.length > 0 && (
              <div className="financial-runtime-warning" role="alert" data-testid="financial-runtime-warning">
                <strong>Advertencia de identidad del runtime</strong>
                <ul>
                  {result.runtimeWarnings.map((warning, index) => (
                    <li key={`${warning}-${index}`}>{warning}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="financial-actions">
              <button type="button" className="btn primary" onClick={handleProcess} disabled={!canProcess}>
                {processing ? 'Procesando...' : 'Procesar y generar estados financieros'}
              </button>
              <a
                className={`btn ghost ${hasDownload ? '' : 'disabled'}`}
                href={hasDownload ? result.downloadUrl : undefined}
                download={result?.outputFilename || 'estados_financieros.xlsx'}
                aria-disabled={!hasDownload}
              >
                Descargar XLSX procesado
              </a>
            </div>
          </article>

          <article className="financial-panel financial-download-panel">
            <div>
              <p className="financial-kicker">Salida</p>
              <h3>Estados financieros</h3>
              <p>
                El archivo descargable contiene Balance General + Estado de Resultados + Balanza,
                generados a partir de la balanza validada.
                Si hay advertencias, revisalas antes de usar el XLSX como entregable final.
              </p>
              <ul className="financial-sheet-list" aria-label="Hojas incluidas">
                <li><strong>BG</strong> · Balance General</li>
                <li><strong>ER</strong> · Estado de Resultados</li>
                <li><strong>BAL</strong> · Balanza</li>
              </ul>
              {result && (
                <small className="financial-download-meta" data-testid="financial-download-meta">
                  Hojas reportadas: {result.sheetNames.join(', ') || 'No reportadas'} · SHA-256: {result.outputSha256Short || 'No reportado'} · Generado: {formatGeneratedAt(result.generatedAt)}
                </small>
              )}
            </div>
            <StatusPill state={hasDownload ? 'ready' : result?.generated ? 'warning' : 'planned'}>
              {hasDownload ? 'Listo para descarga' : result?.generated ? 'Pendiente enlace' : 'Sin archivo'}
            </StatusPill>
          </article>
        </div>

        <aside className="financial-side">
          <article className="financial-panel financial-summary">
            <div className="financial-panel-head compact">
              <div>
                <p className="financial-kicker">Revision</p>
                <h3>Validaciones principales</h3>
              </div>
              <StatusPill state={result?.validationOk ? 'ready' : result ? 'warning' : 'planned'}>
                {result ? (result.validationOk ? 'Aprobado' : 'Con alertas') : 'Sin procesar'}
              </StatusPill>
            </div>

            <div className="financial-validation-list">
              {validationRows.map(row => (
                <ValidationRow
                  key={row.label}
                  label={row.label}
                  status={row.status}
                  state={row.state}
                  detail={row.detail}
                />
              ))}
            </div>
          </article>

          <article className="financial-panel financial-notes">
            <p className="financial-kicker">Observaciones</p>
            {result?.warnings?.length ? (
              <ul>
                {result.warnings.slice(0, 5).map((warning, index) => (
                  <li key={`${warning}-${index}`}>{warning}</li>
                ))}
              </ul>
            ) : (
              <p>{result ? 'Sin observaciones adicionales para esta corrida.' : 'Las observaciones apareceran despues de procesar la balanza.'}</p>
            )}
          </article>
        </aside>
      </div>
    </section>
  );
}

export default EstadosFinancierosView;
